/**
 * -----------------------------------------------------------------------------
 * White Label Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all white label domains
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useListEnterprisesQuery } from "../../api/network/sysadminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../api/store/commonSlice";
import { OrganizationUser } from "../../api/types/Admin";
import { TEnterprise } from "../../api/types/Sysadmin";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import AdminTable from "../../components/adminTable";
import ListingPageSubHeader from "../../components/listingPageSubHeader";
import ListingTableHeader from "../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../components/modalEditListingColumns";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TAdminTableRowData } from "../../components/types";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { routeUrls } from "../../navigation/routeUrls";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import { useLoggedInUserData } from "../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
// import SysadminEnterprisesCreateNew from "./createNewEnterprise";
import { useNavigate } from "react-router-dom";
import {
  useGetWhiteLabelConfigListQuery,
  useGetWhiteLabelConfigQuery,
} from "../../api/network/adminApiServices";
import { WhiteLabelConfig } from "../../api/types/WhiteLabelConfig";

const all_columns = ["ID", "Brand Name", "Domain"];

const ScreenWhiteLabeling = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "whiteLabeling",
  });
  const { t: tMain } = useTranslation();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { whiteLabeling: whiteLabelingQueryParams } = listingQueryParams;
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.Enterprises
  );
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // preparing query params
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        whiteLabeling: {
          ...whiteLabelingQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const navigate = useNavigate();

  const onViewDetailClick = (id?: number | undefined, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (id) {
      navigate(
        `${routeUrls.dashboardChildren.whiteLabelChildren.whitelabel_settings}/${id}`
      );
    } else {
    }
  };

  const onAddNewClick = (e?: React.MouseEvent) => {
    navigate(routeUrls.dashboardChildren.whiteLabelChildren.add_new);
  };

  // fetching user data
  const {
    data: dataWhiteLabelConfig,
    isFetching: isFetchingEnterprises,
    error,
  } = useGetWhiteLabelConfigListQuery(whiteLabelingQueryParams);
  const { count, next, previous, results } = dataWhiteLabelConfig || {};

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as WhiteLabelConfig[])).map(
        (item: WhiteLabelConfig, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.whiteLabelChildren.whitelabel_settings}/${item.id}`,
          cellData: [
            item?.id, // id:
            item?.brand_name || "-", // Brand name:
            item?.domain, // domain
          ],
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_SYSADMIN_ACCOUNTS) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: onAddNewClick,
          })}
        />

        <ListingTableHeader
          heading={t("listing_heading")}
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={whiteLabelingQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />

        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Enterprises}
        />
        <div className="py-4 mt-4 relative">
          {/* <EditListingColumnsButton /> */}

          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingEnterprises}
            listingQueryParams={whiteLabelingQueryParams}
          />
          {!isFetchingEnterprises && (
            <Pagination
              pageSize={whiteLabelingQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    whiteLabeling: {
                      ...whiteLabelingQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count
                  ? Math.ceil(count / whiteLabelingQueryParams.page_size)
                  : 1
              }
              forcePage={whiteLabelingQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    whiteLabeling: {
                      ...whiteLabelingQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={whiteLabelingQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      {/* <SysadminEnterprisesCreateNew /> */}
    </>
  );
};

export default ScreenWhiteLabeling;
